import React, { useState, useEffect, useRef } from 'react'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { Pencil, Trash2 } from 'lucide-react'
import axios from '../../../../config/axios'
import detailsIcon from '../../../../assets/router/details.svg'
import RegisterFamilyModal from '../RegisterFamilyModal'
import ViewFamilyModal from '../ViewFamilyModal'
import {
  FamiliesContainer,
  TableWrapper,
  StyledTable,
  Th,
  TableRow,
  Td,
  ActionsCell,
  IconButton,
  EmptyStateRow,
  MessageContainer,
} from './style'

import styled from 'styled-components'

const CellContainer = styled.div`
  max-width: 220px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
  width: 100%;
`;

const Tooltip = styled.div<{ visible: boolean; x: number; y: number }>`
  position: fixed;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  background: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 1000;
  max-width: 360px;
  white-space: pre-wrap;
  word-wrap: break-word;
  display: ${props => props.visible ? 'block' : 'none'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: 10px;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid #333;
  }
`;

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
const FASHION_API_URL = `${GATEWAY_URL}/backend-fashion-orders/fashion-product`

interface FamilyData {
  id: number
  name: string
  collection: string
  group: string
  gradeTypes: string
  grades: string
  fabrics: string
  trimmings: string
}

const FamiliesTable: React.FC = () => {
  const navigate = useNavigate()
  const { refresh, isLoading: parentLoading, setIsLoading } = useOutletContext<any>()
  const [families, setFamilies] = useState<FamilyData[]>([])
  const [visibleCount, setVisibleCount] = useState<number>(10)
  const [totalFamilies, setTotalFamilies] = useState<number | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [selectedFamily, setSelectedFamily] = useState<FamilyData | null>(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [tooltipContent, setTooltipContent] = useState('')
  const cellRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  const handleCellMouseEnter = (e: React.MouseEvent, content: string, key: string, id: number) => {
    const ref = cellRefs.current[`${key}-${id}`]
    if (ref && content) {
      const isOverflowing = ref.scrollWidth > ref.clientWidth
      const items = content.split(',').map(s => s.trim()).filter(Boolean)
      if (isOverflowing || items.length > 1) {
        setTooltipContent(items.length > 1 ? items.join('\n') : content)
        setTooltipPosition({ x: e.clientX + 10, y: e.clientY - 10 })
        setShowTooltip(true)
      }
    }
  }

  const handleCellMouseLeave = () => {
    setShowTooltip(false)
  }

  const getFirstWithEllipsis = (content: string) => {
    if (!content) return '-'
    const parts = content.split(',').map(s => s.trim()).filter(Boolean)
    if (parts.length === 0) return '-'
    if (parts.length === 1) return parts[0]
    return `${parts[0]}...`
  }

  const fetchFamilies = async () => {
    setLoading(true)
    if (setIsLoading) setIsLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${FASHION_API_URL}/families`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          limit: visibleCount,
          offset: 0,
          search: undefined,
        },
      })


      let backendData: any[] = []
      if (Array.isArray(response.data)) {
        backendData = response.data
        setTotalFamilies(undefined)
      } else if (response.data && Array.isArray(response.data.content)) {
        backendData = response.data.content
        const total = response.data.totalElements
        setTotalFamilies(typeof total === 'number' ? total : undefined)
      }

      const fetchedFamilies: FamilyData[] = backendData.map((item: any) => {
        const collectionName = item.Collection?.name || '-'

        const gradeItems = item.GradeInFamily || []
        const gradeNames = gradeItems.map((g: any) => g.Grade?.name).filter(Boolean)
        const gradesStr = gradeNames.length > 0 ? gradeNames.join(', ') : '-'
        
        const gradeTypes = [...new Set(gradeItems.map((g: any) => g.Grade?.GraduationType?.name).filter(Boolean))]
        const gradeTypesStr = gradeTypes.length > 0 ? gradeTypes.join(', ') : '-'

        const familyMaterials = item.FamilyMaterial || []
        const fabricNames = familyMaterials
          .map((fm: any) => fm.CollectionMaterial?.Material?.name)
          .filter(Boolean)
        const fabricsStr = fabricNames.length > 0 ? fabricNames.join(', ') : '-'

        const trimmingNames = familyMaterials
          .flatMap((fm: any) => (fm.FamilyMaterialResources || []).map((r: any) => r.Resource?.name))
          .filter(Boolean)
        const uniqueTrimmings = [...new Set(trimmingNames)]
        const trimmingsStr = uniqueTrimmings.length > 0 ? uniqueTrimmings.join(', ') : '-'

        const groupItems = item.GroupInFamily || []
        const groupNames = groupItems.map((g: any) => g.Group?.name).filter(Boolean)
        const groupsStr = groupNames.length > 0 ? groupNames.join(', ') : '-'

        return {
          id: item.id,
          name: item.name || 'Sem nome',
          collection: collectionName,
          group: groupsStr,
          gradeTypes: gradeTypesStr,
          grades: gradesStr,
          fabrics: fabricsStr,
          trimmings: trimmingsStr,
        }
      })

      fetchedFamilies.sort((a, b) => b.id - a.id)

      setFamilies(fetchedFamilies)
      setLoading(false)
      if (setIsLoading) setIsLoading(false)
    } catch (err: any) {
      console.error('Erro ao buscar famílias:', err)
      setError(
        err.response?.data?.message ||
          'Erro ao carregar famílias. Tente novamente.',
      )
      setLoading(false)
      if (setIsLoading) setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchFamilies()
  }, [refresh, visibleCount])

  const handleView = (family: FamilyData) => {
    setSelectedFamily(family)
    setIsViewModalOpen(true)
  }

  const handleEdit = (family: FamilyData) => {
    setSelectedFamily(family)
    setIsEditModalOpen(true)
  }

  const handleEditFromView = () => {
    setIsViewModalOpen(false)
    setIsEditModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta família?')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${FASHION_API_URL}/families/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      alert('Família excluída com sucesso!')

      fetchFamilies()
    } catch (error: any) {
      console.error('Erro ao excluir família:', error)
      alert(
        error.response?.data?.message ||
          'Erro ao excluir família. Tente novamente.',
      )
    }
  }

  const handleModalClose = () => {
    setIsEditModalOpen(false)
    setIsViewModalOpen(false)
    setSelectedFamily(null)
    fetchFamilies()
  }

  if (loading) {
    return (
      <FamiliesContainer>
        <MessageContainer>
          <p>Carregando famílias...</p>
        </MessageContainer>
      </FamiliesContainer>
    )
  }

  if (error) {
    return (
      <FamiliesContainer>
        <MessageContainer style={{ color: 'red' }}>
          <p>{error}</p>
        </MessageContainer>
      </FamiliesContainer>
    )
  }

  if (families.length === 0) {
    return (
      <FamiliesContainer>
        <MessageContainer>
          <p>Nenhuma família encontrada.</p>
        </MessageContainer>
      </FamiliesContainer>
    )
  }

  return (
    <FamiliesContainer>
      <TableWrapper>
        <StyledTable>
          <thead>
            <tr>
              <Th>Família</Th>
              <Th>Coleção</Th>
              <Th>Grupo</Th>
              <Th>Tipo de graduação</Th>
              <Th>Grades</Th>
              <Th>Tecidos</Th>
              <Th>Aviamentos</Th>
              <Th style={{ width: '120px' }}></Th>
            </tr>
          </thead>
          <tbody>
            {families.map((family) => (
              <TableRow key={family.id}>
                <Td>{family.name}</Td>
                <Td>{family.collection}</Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`group-${family.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellMouseEnter(e, family.group, 'group', family.id)}
                    onMouseLeave={handleCellMouseLeave}
                  >
                    {getFirstWithEllipsis(family.group)}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`gradeTypes-${family.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellMouseEnter(e, family.gradeTypes, 'gradeTypes', family.id)}
                    onMouseLeave={handleCellMouseLeave}
                  >
                    {getFirstWithEllipsis(family.gradeTypes)}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`grades-${family.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellMouseEnter(e, family.grades, 'grades', family.id)}
                    onMouseLeave={handleCellMouseLeave}
                  >
                    {getFirstWithEllipsis(family.grades)}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`fabrics-${family.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellMouseEnter(e, family.fabrics, 'fabrics', family.id)}
                    onMouseLeave={handleCellMouseLeave}
                  >
                    {getFirstWithEllipsis(family.fabrics)}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`trimmings-${family.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellMouseEnter(e, family.trimmings, 'trimmings', family.id)}
                    onMouseLeave={handleCellMouseLeave}
                  >
                    {getFirstWithEllipsis(family.trimmings)}
                  </CellContainer>
                </Td>
                <Td>
                  <ActionsCell>
                    <IconButton
                      className='view'
                      onClick={() => handleView(family)}
                      title='Visualizar'
                    >
                      <img src={detailsIcon} alt='Visualizar' />
                    </IconButton>
                    <IconButton
                      className='edit'
                      onClick={() => handleEdit(family)}
                      title='Editar'
                    >
                      <Pencil />
                    </IconButton>
                    <IconButton
                      className='delete'
                      onClick={() => handleDelete(family.id)}
                      title='Excluir'
                    >
                      <Trash2 />
                    </IconButton>
                  </ActionsCell>
                </Td>
              </TableRow>
            ))}
          </tbody>
        </StyledTable>
        <LoadMoreButton
          currentCount={families.length}
          totalCount={totalFamilies}
          hasMore={typeof totalFamilies === 'number' ? families.length < totalFamilies : false}
          onLoadMore={() => setVisibleCount((prev) => prev + 10)}
        />
      </TableWrapper>

      <Tooltip visible={showTooltip} x={tooltipPosition.x} y={tooltipPosition.y}>
        {tooltipContent}
      </Tooltip>

      {selectedFamily && (
        <ViewFamilyModal
          isOpen={isViewModalOpen}
          onClose={handleModalClose}
          onEdit={handleEditFromView}
          familyId={selectedFamily.id}
        />
      )}

      {selectedFamily && (
        <RegisterFamilyModal
          isOpen={isEditModalOpen}
          onClose={handleModalClose}
          onSuccess={(saved) => {

            fetchFamilies()

            if (saved && selectedFamily && saved.id === selectedFamily.id) {
              setSelectedFamily(saved)
            }
          }}
          mode="edit"
          familyId={selectedFamily.id}
        />
      )}
    </FamiliesContainer>
  )
}

export default FamiliesTable